/**
 * Clase que permite implementar formularios.
 *
 * @example de implementacion simple:
 * export class ViewWebOptions extends ViewPromiseController {
 *   constructor() {
 *     super({
 *       label: edicom.locale.translate('global.titulo'),
 *       icon: 'unicorn',
 *       promise: {
 *         getData: () => [1, 2, 3],
 *         template: '<ewc-repeat></ewc-repeat>',
 *       },
 *     });
 *   }
 *
 *   [ViewPromiseController.$onResolved](res) {
 *     console.log(res);
 *   }
 * }
 *
 * @extends ViewController
 */
class ViewPromiseController extends ViewController {
  static $properties = Symbol.for('ViewPromiseController.properties');
  static $onConnected = ViewController.$onConnected;
  static $onDestroy = ViewController.$onDestroy;
  static $render = ViewController.$render;
  static $templateContext = ViewController.$templateContext;
  static $events = Symbol.for('ViewPromiseController.events');
  static $watchers = Symbol.for('ViewPromiseController.watchers');
  // Propio del promise:
  static $onResolved = Symbol.for('ViewPromiseController.onResolved');
  static $onRejected = Symbol.for('ViewPromiseController.onRejected');

  /** @type {function|undefined} sirve para almacenar la promesa que debe devolver los datos */
  #getData;

  /**
   *
   * @param {object} params
   * @param {Object} params.promise
   * @param {() => any} params.promise.getData
   * @param {string} params.promise.template el template que se va pintar solo cuando se resolvera la promesa con exito
   * @param {string} [params.promise.as] el nombre de los datos de respuesta para usar en el `promise.template`. Ej 'data'
   */
  constructor(params) {
    super({
      ...params,
      template: `
        <div slot="main">
          <x-await promise="this.$viewPromiseProperty">
            <x-loading><ewc-loader></ewc-loader></x-loading>
            <x-then as="${params.promise?.as ?? 'data'}">${params.promise?.template ?? ''}</x-then>
            <x-catch><ewc-message-center icon="triangle-exclamation" muted></ewc-message-center></x-catch>
          </x-await>
        </div>
        ${params.template ?? ''}`,
    });

    if (typeof params.promise?.getData === 'function') {
      this.#getData = params.promise?.getData;
      this.$refresh();
    }
  }

  /**
   * Recall - vuelve a pedir los datos y actualiza el template
   */
  $refresh() {
    const getData = this.#getData;
    this.$viewPromiseProperty = Promise.resolve().then(() => {
      return getData?.call(this);
    });
  }

  static get [ViewController.$properties]() {
    return {
      ...this[ViewPromiseController.$properties],
      $viewPromiseProperty: { type: 'Promise' },
    };
  }

  get [ViewController.$onResolved]() {
    return {
      $viewPromiseProperty: async () => {
        try {
          const data = await this.$viewPromiseProperty;
          this[ViewPromiseController.$onResolved]?.(data);
        } catch (error) {
          this[ViewPromiseController.$onRejected]?.(error);
        }
      },
    };
  }

  get [ViewController.$onRejected]() {
    return {
      $viewPromiseProperty: (error) => {
        this[ViewPromiseController.$onRejected]?.(error);
      },
    };
  }

  get [ViewController.$events]() {
    return {
      ...this[ViewPromiseController.$events],
    };
  }

  get [ViewController.$watchers]() {
    return {
      ...this[ViewPromiseController.$watchers],
    };
  }
}

globalThis.ViewPromiseController = ViewPromiseController;
